/* eslint-disable react/jsx-no-bind */

import { useEffect, useRef, useState } from "react";

import Button from "~/components/shared/Button";

import PipelineTemplateFormSelect from "~/components/shared/PipelineTemplateFormSelect";

import { LegacyStepsEditor } from "~/components/pipeline/LegacyStepsEditor";

type PipelineTemplate = {
  uuid?: string;
  name: string;
  description?: string | null;
  configuration: string;
};

export type Props = {
  currentConfiguration: string;
  currentPipelineTemplateUuid: string;
  pipelineTemplates: Array<PipelineTemplate>;
  pipelineTemplatesRequired: boolean;
  updatePipelineStepsPath: string;
  sidebarTrackingContext: {
    organizationUuid: string;
  };
  examplesPath: string;
};

const StepSettingsForm = (props: Props) => {
  const formRef = useRef<HTMLFormElement>(null);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "s" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      if (formRef.current) {
        formRef.current.submit();
      }
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const hasTemplates = props.pipelineTemplates.length > 0;
  const noTemplate: PipelineTemplate = {
    name: "Do not use a pipeline template",
    configuration: props.currentConfiguration,
  };

  // Provide a no template option when a pipeline does not have a template assigned or
  // an organization has not required templates for all pipelines
  const pipelineTemplates =
    props.pipelineTemplatesRequired && props.currentPipelineTemplateUuid
      ? props.pipelineTemplates
      : [noTemplate, ...props.pipelineTemplates];

  const [selectedTemplate, setSelectedTemplate] = useState(
    () =>
      props.pipelineTemplates.find(
        (template) => template.uuid === props.currentPipelineTemplateUuid,
      ) || noTemplate,
  );

  const isEditable = !selectedTemplate.uuid && !props.pipelineTemplatesRequired;

  function handleSelectedTemplateChange(template: PipelineTemplate) {
    setSelectedTemplate(template);
  }

  return (
    <form
      ref={formRef}
      action={props.updatePipelineStepsPath}
      method="post"
      name="edit-pipeline-template"
    >
      <input type="hidden" name="_method" value="patch" />
      <input type="hidden" name="utf8" value="✓" />
      <input type="hidden" name={window._csrf.param} value={window._csrf.token} />

      {hasTemplates && (
        <div className="mb-2" style={{ width: "400px" }}>
          <PipelineTemplateFormSelect
            name="pipeline_template_uuid"
            selected={selectedTemplate}
            items={pipelineTemplates}
            onChange={handleSelectedTemplateChange}
          />
        </div>
      )}

      <LegacyStepsEditor
        name="configuration"
        readOnly={!isEditable}
        value={selectedTemplate?.configuration || props.currentConfiguration}
        trackingContext={props.sidebarTrackingContext}
        autofocus={true}
        examplesPath={props.examplesPath}
        className="!min-h-[800px]"
      >
        <Button type="submit" theme="primary">
          Save Steps
        </Button>
        <Button type="submit" name="commit" value="Save and Build" theme="default">
          Save and Build
        </Button>
      </LegacyStepsEditor>
    </form>
  );
};

export default StepSettingsForm;
